import React, { useState, useEffect } from "react";
import {
  Avatar,
  CircularProgress,
  Popover,
  Typography,
  Card,
} from "@material-ui/core";
import { PrimaryText } from "components/Common/PrimaryText";
import {
  Person,
  AccessTime,
  Favorite,
  Message,
  Chat,
  TouchApp,
  VerifiedUser,
  TrendingUp,
} from "@material-ui/icons";
import { useDisplay } from "context/DisplayContext";

const LiveMetricsCard = () => {
  const { isMobile } = useDisplay();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentLogins, setRecentLogins] = useState([]);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverUser, setPopoverUser] = useState(null);

  const fetchLiveMetrics = async () => {
    try {
      const { getLiveMetrics } = require("services/retention-service");
      const data = await getLiveMetrics();
      setMetrics(data);
      setRecentUsers(data.recentUsers || []);
      setRecentLogins(data.recentLogins || []);
    } catch (error) {
      console.error("Error fetching live metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePopoverOpen = (event, user) => {
    event.stopPropagation();
    // If clicking on the same user, close the popover
    if (popoverUser?.uid === user.uid) {
      setPopoverAnchor(null);
      setPopoverUser(null);
    } else {
      setPopoverAnchor(event.currentTarget);
      setPopoverUser(user);
    }
  };

  const handlePopoverClose = (event) => {
    event?.stopPropagation();
    // Only clear the anchor, keep the user data to prevent showing "Anonymous"
    setPopoverAnchor(null);
    // Clear user data after a short delay to prevent the flash
    setTimeout(() => setPopoverUser(null), 100);
  };

  // Helper function to convert Firestore timestamp to Date
  const convertTimestampToDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp._seconds !== undefined) {
      // Firestore timestamp format
      return new Date(
        timestamp._seconds * 1000 + timestamp._nanoseconds / 1000000
      );
    }
    // Regular Date object or string
    return new Date(timestamp);
  };

  useEffect(() => {
    fetchLiveMetrics();

    // Poll every 10 seconds
    const interval = setInterval(fetchLiveMetrics, 10000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card style={{ padding: 20, textAlign: "center", height: isMobile ? "auto" : 400 }}>
        <CircularProgress />
        <PrimaryText>Loading live metrics...</PrimaryText>
      </Card>
    );
  }

  return (
    <Card style={{ padding: isMobile ? 12 : 20, height: isMobile ? "auto" : 400, overflow: "auto" }}>
      <PrimaryText fontSize={isMobile ? 16 : 20} fontWeight={700} style={{ marginBottom: 20 }}>
        Live Activity
      </PrimaryText>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Last User Signed Up */}
        {metrics?.lastUser && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar
              src={metrics.lastUser.photoURL}
              style={{ width: 48, height: 48, cursor: "pointer" }}
              onClick={(e) => handlePopoverOpen(e, metrics.lastUser)}
            >
              <Person />
            </Avatar>
            <div style={{ flex: 1 }}>
              <PrimaryText fontSize={14} fontWeight={600}>
                {metrics.lastUser.displayName ||
                  metrics.lastUser.email ||
                  "Anonymous User"}
              </PrimaryText>
              <br />
              <PrimaryText fontSize={12} color="#666">
                Last signed up {metrics.lastUser.timeAgo}
              </PrimaryText>
            </div>
          </div>
        )}

        {/* Activity Metrics */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <AccessTime style={{ color: "#1976d2", fontSize: 20 }} />
              <div>
                <PrimaryText fontSize={16} fontWeight={600}>
                  {metrics?.activeLastHour || 0}
                </PrimaryText>
                <PrimaryText fontSize={11} color="#666">
                  Active last hour
                </PrimaryText>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <AccessTime style={{ color: "#7b1fa2", fontSize: 20 }} />
              <div>
                <PrimaryText fontSize={16} fontWeight={600}>
                  {metrics?.activeLast24Hours || 0}
                </PrimaryText>
                <PrimaryText fontSize={11} color="#666">
                  Active last 24h
                </PrimaryText>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Metrics */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Favorite style={{ color: "#e91e63", fontSize: 20 }} />
              <div>
                <PrimaryText fontSize={16} fontWeight={600}>
                  {metrics?.matchesLastHour || 0}
                </PrimaryText>
                <PrimaryText fontSize={11} color="#666">
                  Matches last hour
                </PrimaryText>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Message style={{ color: "#ff9800", fontSize: 20 }} />
              <div>
                <PrimaryText fontSize={16} fontWeight={600}>
                  {metrics?.messagesLastHour || 0}
                </PrimaryText>
                <PrimaryText fontSize={11} color="#666">
                  Messages last hour
                </PrimaryText>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Chat style={{ color: "#4caf50", fontSize: 20 }} />
              <div>
                <PrimaryText fontSize={16} fontWeight={600}>
                  {metrics?.conversationsToday || 0}
                </PrimaryText>
                <PrimaryText fontSize={11} color="#666">
                  New conversations
                </PrimaryText>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TouchApp style={{ color: "#2196f3", fontSize: 20 }} />
              <div>
                <PrimaryText fontSize={16} fontWeight={600}>
                  {metrics?.swipesLastHour || 0}
                </PrimaryText>
                <PrimaryText fontSize={11} color="#666">
                  Swipes last hour
                </PrimaryText>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Signups */}
        {recentUsers.length > 0 && (
          <div>
            <PrimaryText
              fontSize={14}
              fontWeight={600}
              style={{ marginBottom: 8 }}
            >
              Recent Signups
            </PrimaryText>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {recentUsers.slice(0, 8).map((user, index) => (
                <Avatar
                  key={index}
                  src={user.photoURL}
                  style={{ width: 32, height: 32, cursor: "pointer" }}
                  onClick={(e) => handlePopoverOpen(e, user)}
                >
                  <Person style={{ fontSize: 18 }} />
                </Avatar>
              ))}
              {recentUsers.length > 8 && (
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#666",
                  }}
                >
                  +{recentUsers.length - 8}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Logins */}
        {recentLogins.length > 0 && (
          <div>
            <PrimaryText
              fontSize={14}
              fontWeight={600}
              style={{ marginBottom: 8 }}
            >
              Recent Logins
            </PrimaryText>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {recentLogins.slice(0, 8).map((user, index) => (
                <Avatar
                  key={index}
                  src={user.photoURL}
                  style={{ width: 32, height: 32, cursor: "pointer" }}
                  onClick={(e) => handlePopoverOpen(e, user)}
                >
                  <Person style={{ fontSize: 18 }} />
                </Avatar>
              ))}
              {recentLogins.length > 8 && (
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#666",
                  }}
                >
                  +{recentLogins.length - 8}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bottom Stats */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: 8,
            borderTop: "1px solid #eee",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <VerifiedUser style={{ color: "#4caf50", fontSize: 18 }} />
            <PrimaryText fontSize={12}>
              {metrics?.verificationsToday || 0} verified today
            </PrimaryText>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                animation: "pulse 2s infinite",
              }}
            />
            <PrimaryText fontSize={12}>
              {metrics?.onlineNow || 0} online
            </PrimaryText>
          </div>
        </div>

        {/* Peak Activity Time */}
        {metrics?.peakActivityTime && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              paddingTop: 8,
            }}
          >
            <TrendingUp style={{ color: "#ff5722", fontSize: 18 }} />
            <PrimaryText fontSize={12} color="#666">
              Peak: {metrics.peakActivityTime}
            </PrimaryText>
          </div>
        )}

        {/* User Info Popover */}
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <div style={{ padding: 12, maxWidth: 200 }}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {popoverUser?.displayName ||
                popoverUser?.email ||
                "Anonymous User"}
            </Typography>
            {popoverUser?.email && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 2 }}
              >
                {popoverUser.email}
              </Typography>
            )}
            {popoverUser?.createdAt && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 2 }}
              >
                Joined:{" "}
                {convertTimestampToDate(
                  popoverUser.createdAt
                )?.toLocaleDateString()}
              </Typography>
            )}
            {popoverUser?.lastLogin && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 2 }}
              >
                Last login:{" "}
                {convertTimestampToDate(
                  popoverUser.lastLogin
                )?.toLocaleDateString()}
              </Typography>
            )}
          </div>
        </Popover>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
          }
        }
      `}</style>
    </Card>
  );
};

export default LiveMetricsCard;
